import {
  type ComponentPropsWithoutRef,
  type ForwardedRef,
  forwardRef,
  type HTMLAttributes,
} from 'react';
import styled, { css } from 'styled-components';

import { cn } from '@/lib/cn';
import { modalStyle } from '@/styles/style';

import { Flex, VFlex } from '../base';
import Button from '../Button/Button';
import type { ButtonT } from '../Button/Button/types';
import Heading from '../Typography/Heading';

export const ModalHeader = (props: HTMLAttributes<HTMLDivElement>) => {
  const { children, className, ...rest } = props;
  return (
    <div {...rest} className={cn('flex flex-col gap-3 p-7 pb-6', className)}>
      {children}
    </div>
  );
};

type FooterProps = {
  confirm?: ButtonT;
  cancel?: ButtonT;
  additional?: ButtonT;
  children?: React.ReactNode;
};

export const ModalFooter = (props: FooterProps) => {
  return (
    <Flex padding="24px 28px 28px 28px" j="space-between" a="center">
      <Flex>
        {props.additional && (
          <Button
            variant="secondary"
            sizeType="m"
            {...props.additional}
            children={props.additional.children || 'Additional'}
          />
        )}
      </Flex>
      <Flex g={10}>
        {props.cancel && (
          <Button
            variant="secondary"
            sizeType="m"
            {...props.cancel}
            children={props.cancel.children || 'Cancel'}
          />
        )}
        {props.confirm && (
          <Button
            variant="primary"
            sizeType="m"
            {...props.confirm}
            children={props.confirm.children || 'Confirm'}
          />
        )}
      </Flex>
      {props.children}
    </Flex>
  );
};

export const ModalBody = forwardRef(
  (
    props: ComponentPropsWithoutRef<'div'>,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    return (
      <div
        {...props}
        className={cn('overflow-auto px-7', props.className)}
        ref={ref}
      >
        {props.children}
      </div>
    );
  },
);

export const ModalLayout = styled(VFlex)<{ modalSize?: ModalSizeT }>`
  ${({ modalSize }) =>
    modalSize &&
    css`
      width: ${getModalSize(modalSize)}px;
    `}
  ${modalStyle};
`;

export const ModalTitle = styled(Heading).attrs({
  type: 'h3',
})``;

export const ModalDesc = styled.span`
  font-size: 14px;
  line-height: 1.2;
  font-weight: 500;
`;

export const ModalConfirm = styled(Button).attrs({
  variant: 'primary',
  sizeType: 'm',
})``;

export const ModalCancel = styled(Button).attrs({
  variant: 'secondary',
  sizeType: 'm',
})``;

export const ModalAdditional = styled(Button).attrs({
  variant: 'secondary',
  sizeType: 'm',
})``;

export type ModalSizeT = 's' | 'm' | 'l';

export const getModalSize = (size: ModalSizeT) => {
  switch (size) {
    case 's':
      return 480;
    case 'm':
      return 680;
    case 'l':
      return 1000;
  }
};
